<template>
  <div
    ref="wooVideo"
    class="woo-video"
    :style="{ transform: `translate(${offsetMoveX}, ${offsetMoveY})` }"
    :class="{ 'repeat-notification-active': showRepeatNotification }"
  >
    <video-overlay :overlays="currentOverlays"/>
    <video ref="backgroundMusicPlayer" style="display: none;">
      <source :src="options.backgroundMusicSrc" type="audio/mp3"/>
    </video>
    <video playsinline ref="videoPlayer" :class="{ 'video-js': true, 'video-js-flip': options.flip }"/>
    <div class="woo-video-header">
      <div class="woo-video-header-navigation">
        <slot></slot>
      </div>
      <div class="woo-video-header-title">
        <div style="flex: 1; text-align: left;">
          <span class="woo-video-header-title-wrapper">
            {{ this.title }}
          </span>
        </div>
        <div class="woo-video-header-title-actions">
          <v-icon class="close" @click="onClose"> mdi-close </v-icon>
        </div>
      </div>
    </div>
    <v-row class="woo-video-actions">
      <v-col align="left">
        <v-icon
          @click="playPreviousVideo()"
          v-if="!hidePrevButton"
        >
          mdi-arrow-left
        </v-icon>
      </v-col>
      <v-col>
        <v-icon @click="showHelp()" class="help">
          mdi-help-circle-outline
        </v-icon>
      </v-col>
      <v-col align="right">
        <v-icon @click="playNextVideo()" class="next"> mdi-arrow-right </v-icon>
      </v-col>
    </v-row>
    <div
      class="woo-video-tutorial-help"
      @click="triggerReplay"
      v-if="showRepeatNotification"
    >
      <div
        class="woo-video-tutorial-help-box"
        :class="{
          showProgress: slideInProgressBar,
        }"
      >
        <div
          class="replay-progress"
          :class="{
            showProgress: startProgressBar,
          }"
        ></div>
        <v-row>
          <v-col
            :class="{
              repeatBar: showRepeatBar,
            }"
          >
            <v-icon> mdi-replay </v-icon>
          </v-col>
          <v-col cols="8" align="center" v-if="!showRepeatBar">
            Repeating in {{ countDownTime }}...
          </v-col>
          <v-col v-if="!showRepeatBar"></v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>
<script>
import videojs from 'video.js';
import hotkeys from 'videojs-hotkeys';
import 'video.js/dist/video-js.css';

import { LOCAL_STORAGE_KEYS } from '@/constants';
import VideoOverlay from './VideoOverlay.vue';
import EventHub, { Events } from '@/helpers/EventHub';
import HelperMethods from '@/helpers/HelperMethods';

const PLAYBACK_RATES = [1,2,4,8];

export default {
  name: 'VideoPlayer',
  components: { VideoOverlay },
  props: {
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    event: {
      type: Object,
      default() {
        return {};
      },
    },
    title: {
      type: String,
    },
    horizontalSwipe: {
      type: Boolean,
    },
    verticalSwipe: {
      type: Boolean,
    },
    manualLoop: {
      type: Boolean,
    },
    isHelpVideo: {
      type: Boolean,
    },
    hidePrevButton: {
      type: Boolean,
    },
    showRepeatBar: {
      type: Boolean,
    },
  },
  data() {
    return {
      treshold: 100,
      tresholdTutorial: 20,
      touchStartY: undefined,
      touchEndY: undefined,
      touchStartX: undefined,
      touchEndX: undefined,
      offsetMoveX: 0,
      offsetMoveY: 0,
      tapped: null,
      player: undefined,
      showOnScreenTutorial: false,
      repeatTreshold: 3,
      showRepeatNotification: false,
      startProgressBar: false,
      slideInProgressBar: false,
      countDownInterval: undefined,
      countDownTime: undefined,
      currentTime: undefined,
      playerTimeSeconds: 0,
    };
  },
  computed: {
    currentOverlays() {
      if (this.playerTimeSeconds < 0) return [];

      // check if we need any overlays here
      const currentOverlays = (this.options.overlays ?? []).filter(o => {
        return o.start_time <= this.playerTimeSeconds && this.playerTimeSeconds <= o.end_time
      });

      return currentOverlays;
    },
    isPaused() {
      if (this.player) {
        //NOTE: there is an error witih the videojs library where it doesn't update the current 
        // time of the video. And unless we're not calling it it will not return correctly
        this.player.currentTime();
        if (this.player.paused() && this.player.currentTime() > 0) {
          return 'play';
        } else {
          return '';
        }
      } else {
        return '';
      }
    },
    onScreenTutorialPassed() {
      return !!localStorage.getItem(LOCAL_STORAGE_KEYS.ON_SCREEN_TUTORIAL_PASSED);
    },
    subtitlesOn() {
      return this.$store.state.subtitlesOn;
    },
    playbackRateOn() {
      return this.$store.state.playbackRateOn;
    },
    playbackRate() {
      return this.$store.state.playbackRate;
    },
    hasSubtitles() {
      return this.options.tracks?.length > 0
    },
  },
  mounted() {
    window.addEventListener('popstate', () => {
      this.clearReply();
    });

    if (!this.options.raw_video_url) {
      this.$router.replace({ path: '/404', query: { error: 'Video URL is missing' } });
      return;
    }

    this.player = videojs(this.$refs.videoPlayer, {
      ...this.options,
      controls: true,
      controlBar: {
        subsCapsButton: false,
      },
      autoplay: false,
      pip: false,
      loop: false,
      liveui: true,
      textTrackSettings: false,
      html5: {
        nativeTextTracks: false,
        vhs: {
          overrideNative: true
        }
      },
      preload: 'metadata',
      plugins: {
        hotkeys,
      },
      userActions: {
        doubleClick: false,
      },
      playbackRates: this.playbackRateOn ? PLAYBACK_RATES : [],
    });

    this.player.ready(() => {
      this.player.playbackRate(this.playbackRate || 1);
      this.renderCcButton();
    });

    // If we navigate away from the window, pause the video
    window.addEventListener('blur', this.pause);

    // This is a secondary player to play background music at the same
    // time as the video if needed
    this.backgroundMusicPlayer = videojs(this.$refs.backgroundMusicPlayer, {
      userActions: {
        doubleClick: false
      }
    });

    this.player.reloadSourceOnError({
      errorInterval: 5,
    })

    this.backgroundMusicPlayer.volume(0.03);

    this.player.on('timeupdate', () => {
      const playerTimeSeconds = this.player.currentTime();
      this.playerTimeSeconds = playerTimeSeconds;
      this.currentTime =
        (playerTimeSeconds / this.player.duration()) * 100;
    });

    this.player.on('ended', () => {
      this.showRepeatNotification = true;
      
      if (this.manualLoop) {
        this.countDownTime = this.repeatTreshold;
        setTimeout(() => {
          this.slideInProgressBar = true;
          setTimeout(() => {
            this.startProgressBar = true;
            this.coutdonwInterval = setInterval(() => {
              this.countDownTime = this.countDownTime - 1;

              if (this.countDownTime === 0) {
                EventHub.$emit(Events.ReplayVideo);
                this.player.currentTime(0);
                this.player.play();
                this.clearReply();
              }
            }, 1000);
          }, 250);
        });
      } else {
        this.slideInProgressBar = true;
      }

      if (!this.onScreenTutorialPassed) {
        this.showOnScreenTutorial = true;
        localStorage.setItem(LOCAL_STORAGE_KEYS.ON_SCREEN_TUTORIAL_PASSED, true);
      }

      this.pushEvent({
        event: 'video_ended',
      });
    });
    
    this.player.on('touchstart', (e) => {
      this.tapped = null;
      this.touchStartY = e.touches[0].clientY;
      this.touchStartX = e.touches[0].clientX;
    });
    
    this.player.on('touchend', (e) => {
      this.touchEndY = e.changedTouches[0].clientY;
      this.touchEndX = e.changedTouches[0].clientX;
      this.triggerReplay();
    });

    this.player.on('play', () => {
      HelperMethods.setJsonInLocalStorage({
        key: LOCAL_STORAGE_KEYS.LAST_QUERY,
        json: { ...this.$route.query },
      });

      this.pushEvent({
        event: 'video_played',
      });

      // Only play background music if this is set
      if (this.options.addBackgroundMusic) {
        this.backgroundMusicPlayer.play();
      }
      
      this.clearReply();
    });

    this.player.on('pause', () => {
      this.backgroundMusicPlayer.pause();

      this.pushEvent({
        event: 'video_paused',
      });
    })
  
    this.player.on('ratechange', () => {
      this.$store.commit('playbackRate', this.player.playbackRate());
    })
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }

    if (this.backgroundMusicPlayer) {
      this.backgroundMusicPlayer.dispose();
    }

    window.removeEventListener('blur', this.pause);
  },
  methods: {
    pushEvent({ event }) {
      HelperMethods.postGoogleEvent({
        event,
        video_id: this.$route.query.v,
        step_id: this.$route.query.s,
        video_title: this.$attrs.video.display_name
      });
    },
    triggerReplay() {
      this.clearReply();
      this.toggleVideo();
    },
    clearReply() {
      clearInterval(this.coutdonwInterval);
      this.showRepeatNotification = false;
      this.startProgressBar = false;
      this.slideInProgressBar = false;
    },
    toggleVideo() {
      if (this.player.paused()) {
        this.player.play();
      } else {
        this.player.pause();
      }
    },
    playNextVideo() {
      this.clearReply();

      EventHub.$emit(
        this.isHelpVideo ? Events.PlayNextHelpVideo : Events.PlayNextVideo
      );
    },
    playPreviousVideo() {
      this.clearReply();
      EventHub.$emit(
        this.isHelpVideo
          ? Events.PlayPreviousHelpVideo
          : Events.PlayPreviousVideo
      );
    },
    showHelp() {
      EventHub.$emit(Events.ShowHelp);
    },
    pause() {
      if (!this.player) return
      this.player.pause()
    },
    toggleSubtitles() {
      this.$store.commit('subtitlesOn', !this.subtitlesOn);
    },
    onClose() {
      if (this.$attrs.onClose) {
        this.$attrs.onClose()
      }
    },
    renderCcButton() {
      if (!this.hasSubtitles) return;
      
      if (!this.ccButton) {
        this.ccButton = this.player.controlBar.addChild('button', {}).el();
      }

      this.ccButton.onclick = this.toggleSubtitles
      this.ccButton.ontouchend = e => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleSubtitles();
      }
      
      this.ccButton.innerHTML = `
        <div class="cc-button-wrapper">
          <span class="cc-button ${this.subtitlesOn ? 'active' : ''}">
            CC
          </span>
        </div>
      `;
    }
  },
  watch: {
    subtitlesOn(showing) {
      this.player.textTracks().tracks_.map(t => {
        // When we have multiple languages we'll have to enable 
        // just the one for the given language, for now just show 
        // all of them
        t.mode = showing ? 'showing' : 'hidden';
      });

      this.renderCcButton()
    },
    playbackRateOn(showing) {
      this.player.playbackRates(showing ? PLAYBACK_RATES : []);
    },
    playbackRate() {
      this.player.playbackRate(this.playbackRate || 1);
    }
  }
};
</script>
<style lang="scss" scoped>
@keyframes fadeOut {
  from { opacity :1; }
  to { opacity :0; }
}

.woo {
  &-video {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: calc(100% - 20px);
    
    @media screen and (max-width: 768px) {
      height: 100%;  
    }
    display: none;
    background-color: black;

    &-actions {
      height: 4.5rem;
      padding: 0 1.5rem;
      margin-top: 0;
      z-index: 3;
      button {
        color: white;
        width: 3rem;
        height: 3rem;
        font-weight: 800;
        font-size: 1.5rem;
        line-height: 2rem;
        margin: 0;
      }
    }
    &-tutorial-help {
      position: absolute;
      z-index: 2;
      width: 100%;
      height: 100%;
      pointer-events: none;
      top: 0;
      &-box {
        margin: 0.25rem;
        width: calc(100% - 0.5rem);
        height: 3rem;
        right: 0;
        position: absolute;
        bottom: 4.5rem;
        background: black;
        color: white;
        padding: 0.75rem;
        text-align: left;
        font-weight: 700;
        transform: scaleY(0);
        transition: all 0.25s linear;
        transform-origin: bottom;
        &.showProgress {
          transform: scaleY(1);
        }
        .col {
          z-index: 2;
        }
        .v-icon {
          color: white;
        }
        .replay-progress {
          height: 100%;
          width: 100%;
          transform: scaleX(0);
          background-color: rgba(46, 192, 189, 1);
          transform-origin: left;
          position: absolute;
          left: 0;
          top: 0;
          transition: all 3s linear;
          &.showProgress {
            transform: scaleX(1);
          }
        }
        .col.repeatBar {
          text-align: center;
          background-color: rgba(46, 192, 189, 1);
          cursor: pointer;
        }
      }
      &-image {
        width: 100%;
        height: 100%;
      }
    }
    &-indicator {
      position: absolute;
      z-index: 2;
      opacity: 0.6;
      left: 50%;
      top: 42%;
      transform: translate(-50%, -50%);
      pointer-events: none;
      &.play {
        .play {
          display: block;
        }
      }
      &.pause {
        .pause {
          display: block;
        }
      }
      .v-icon {
        font-size: 10rem;
        color: #fff;
        display: none;
      }
    }
    &.active {
      display: block;
    }
    &.help-video {
      position: absolute;
      // height: calc(90% - 3rem);
      width: 100%;
      margin-right: 1rem;
      background-color: #f2f2f2 !important;
      display: none;
      &.active {
        display: inline-block;
      }
      .video-js {
        width: calc(100% - 0.5rem);
        height: calc(100% - 4.5625rem - 0.5rem);
      }
      .woo-video-actions {
        padding: 0 0.75rem;
        button {
          color: black;
          &.help {
            display: none;
          }
        }
      }
    }
    .video-js {
      width: calc(100% - 0.5rem);
      height: calc(100% - 4.5625rem - 0.5rem);
      margin: 0.25rem;
      border-radius: 0.25rem;
      overflow: hidden;
      video {
        object-fit: cover;
      }
    }

    .video-js-flip > video {
      transform: scale(-1, 1);
    }

    &-header {
      position: absolute;
      left: 0;
      top: 0;
      margin: 0.25rem;
      width: calc(100% - 0.5rem);
      
      &-title {
        display: flex;
        align-items: center;
        color: white;
        font-weight: 700;
        margin: -3px 1rem 0 1rem;
        height: 30px;
        
        &-actions {
          text-align: right;
          margin-right: -22px;

          .close {
            color: white;
          }
        }
        
        &-wrapper {
          background-color: rgba(0, 0, 0, 0.2);
          padding: 0.25rem 0.3125rem;
          border-radius: 0.125rem;
        }
      }
    }
  }
}
</style>
<style lang="scss">
.video-js {
  .vjs-big-play-button {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  .vjs-picture-in-picture-control,
  .vjs-fullscreen-control,
  .vjs-volume-panel {
    display: none;
  }
  .vjs-control-bar {
    background: none;
  }
  .vjs-big-play-button {
    display: none;
  }
  .vjs-slider {
    background: rgba(0, 0, 0, 0.2) !important;
  }
  .vjs-load-progress,
  .vjs-load-progress div {
    background: rgba(0, 0, 0, 0) !important;
  }
  .vjs-play-control {
    pointer-events: none;
  }

  .cc-button-wrapper {
    margin-bottom: 3px;
  }

  .cc-button {
    color: white;
    border: 2px solid white;
    border-radius: 2px;
    padding: 4px 4px 3px 4px;
    font-size: 12px;
    font-weight: bold;

    &.active {
      color: black;
      background-color: white;
    }
  }
}
.repeat-notification-active {
  cursor: pointer;
  .vjs-control-bar {
    display: none !important;
    pointer-events: none !important;
  }
}

.woo-playlist-navigation {
  display: flex;
  width: calc(100% - 2rem);
  margin: 0.75rem 1rem;
  visibility: hidden;
  
  .microstep {
    height: 0.125rem;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.2);
  }
  
  .completed {
    width: 0%;
    height: 2px;
    background-color: #fff;
    position: relative;
    top: -2px;
  }

  .col {
    padding: 0 0.125rem;
    color: white;
  }
  
  button {
    color: white;
  }
  
  &.active {
    visibility: visible;
  }
}
</style>
